import { faMinus as lightMinus } from "@jewlr/pro-light-svg-icons/faMinus"
import { faPlus as lightPlus } from "@jewlr/pro-light-svg-icons/faPlus"
import PropTypes from "prop-types"
import { useLayoutEffect, useRef } from "react"
import { connect } from "react-redux"
import scroll from "scroll"
import scrollDoc from "scroll-doc"

import { fetchSize } from "areas/product/jewlr/modals/helper"
import {
  SizeBoxSmall,
  Size,
} from "areas/product/jewlr/modals/styled-components"
import {
  clearDefault,
  scrollPanelIndex,
  togglePanelIndex,
} from "areas/product/store/actions"
import { SkuIcon } from "areas/product/wizard-options/components"
import Button from "components/ui/button"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import Image from "components/ui/image"
import { builderImage } from "helpers/product/builder-image"
import displayOptionDescription from "helpers/product/display-option-description"
import displayOptionPrice from "helpers/product/display-option-price"
import { imageLookup } from "helpers/product/image-lookup"
import { filterProductOptionsV2 } from "helpers/product/product-filtering"
import { hasHebrewChar } from "helpers/product/text-dir"
import { cn } from "styles/utils"

const MetalIcon = ({ ...props }) => (
  <SkuIcon
    className="text-14 tablet:my-0 tablet:ml-0 tablet:mr-16 my-0 ml-0 mr-16"
    style={{ height: "40px", width: "40px" }}
    {...props}
  />
)

const AccordionThumbImg = (props) => (
  <Image className="tablet:mr-15 mr-16" height="40" width="40" {...props} />
)

const PlusMinus = (props) => (
  <FontAwesomeIcon
    className="text-grey-65 text-16 transition-all duration-200 ease-in-out"
    {...props}
  />
)

const InternalAccordionHeading = ({
  bandColor,
  dataRing,
  description,
  hideMiniview,
  hidePrice,
  image,
  isOpened,
  pref,
  // prefOrder,
  price,
  removeScroll,
  scrollPanel,
  selected,
  togglePanel,
  wizardData,
  wizardHeadingTitle,
}) => {
  const headingRef = useRef()

  const wizardDataHeading = wizardData && wizardData.heading

  useLayoutEffect(() => {
    if (scrollPanel === pref) {
      const page = scrollDoc()
      let pageOffset = hideMiniview ? 80 : 200
      const openedHeading = headingRef.current
      const yOffset = openedHeading.offsetTop - pageOffset
      scroll.top(page, yOffset)
      removeScroll()
    }
  }, [scrollPanel, pref])

  const renderThumb = () => {
    if (selected && dataRing && !dataRing.not_a_ring) {
      var sizeString = fetchSize(selected.option_description)
      if (selected.value === "") {
        return (
          <SizeBoxSmall title="Size">
            <Size />
          </SizeBoxSmall>
        )
      }
      return (
        <SizeBoxSmall title="Size">
          <Size
            dangerouslySetInnerHTML={{
              __html: sizeString,
            }}
          />
        </SizeBoxSmall>
      )
    } else if (/b[0-9]+/.test(wizardDataHeading.pref)) {
      return (
        <div
          className="border-5 border-(--border-color) ml-7 mr-16 rounded-full"
          style={{
            "--border-color": `rgb(${bandColor})`,
            height: "35px",
            width: "35px",
          }}
        />
      )
    } else if (
      (wizardDataHeading.pref === "sku" ||
        /m[0-9]+/.test(wizardDataHeading.pref)) &&
      selected
    ) {
      if (selected.value === "CER") {
        return <MetalIcon skuColor="CE">CE</MetalIcon>
      } else if (selected.value === "VERMY") {
        return <MetalIcon skuColor="V">V</MetalIcon>
      } else if (selected.value === "SS14KYP") {
        return <MetalIcon skuColor="YGP">14K</MetalIcon>
      } else if (
        selected.value === "SS18KYP" ||
        selected.value === "SST18KYP"
      ) {
        return <MetalIcon skuColor="YGP">18K</MetalIcon>
      } else if (selected.value === "SSRGP") {
        return <MetalIcon skuColor="R">R</MetalIcon>
      } else {
        return (
          <MetalIcon skuColor={selected.imageKey?.slice(-1)}>
            {selected.icon_text}
          </MetalIcon>
        )
      }
    } else if (image && image.x1) {
      return (
        <AccordionThumbImg
          alt={description || ""}
          data-sizes="40px"
          src={image.x1}
          srcSet={image.x1 + " 1x, " + image.x2 + " 2x"}
        />
      )
    } else if (typeof image === "string") {
      return (
        <AccordionThumbImg
          alt={description || ""}
          data-sizes="40px"
          src={image}
        />
      )
    } else {
      return (
        <AccordionThumbImg
          alt={description || ""}
          data-sizes="40px"
          src={require("images/icons/jewlr.gif")}
        />
      )
    }
  }

  if (
    !wizardDataHeading ||
    wizardDataHeading.disabled ||
    wizardData.disabled ||
    (/^s[0-9]+/.test(pref) &&
      wizardData.options.length <= 1 &&
      !wizardData.override_text)
  ) {
    return null
  }

  return (
    <>
      {/^face[0-9]+$/.test(wizardDataHeading.pref) && (
        // added face option header
        <div>
          <h2 className="tablet:block text-20 border-b-grey-35 tablet:px-0 tablet:pb-0 tablet:my-16 hidden border-b-2 px-10 pb-5 font-bold uppercase tracking-wider">
            {"SIDE " + wizardDataHeading.pref.replace(/\D/g, "")}
          </h2>

          <div className="py-25 bg-grey-4 border-t-grey-10 tablet:hidden border-t px-20">
            <div className="text-14 border-b-grey-20 tracking-half leading-0 m-0 w-full border-b text-center font-bold uppercase text-black">
              <span className="bg-grey-4 px-10">
                {"SIDE " + wizardDataHeading.pref.replace(/\D/g, "")}
              </span>
            </div>
          </div>
        </div>
      )}
      <div
        className={cn(
          "border-b-grey-10 border-b p-4",
          isOpened ? "bg-polar" : "bg-white"
        )}
        ref={headingRef}
      >
        <h2>
          <Button
            aria-controls={`wizard-accordion-content-${wizardDataHeading.pref}`}
            aria-expanded={isOpened}
            className="tablet:pr-6 flex w-full justify-between py-6 pl-6 pr-14 text-left"
            data-cy={`product-wizard-heading-${wizardDataHeading.pref}`}
            id={`wizard-accordion-heading-${wizardDataHeading.pref}`}
            onClick={() => togglePanel(wizardDataHeading.pref)}
          >
            <div className="flex">
              {renderThumb()}
              <div className={cn("flex flex-col items-start")}>
                <div
                  className={cn(
                    "flex flex-col items-start",
                    wizardDataHeading.pref === "diamond" && "justify-center"
                  )}
                >
                  <p className="text-14 tablet:font-bold tablet:tracking-half m-0 font-semibold uppercase text-black">
                    {wizardHeadingTitle}
                  </p>
                  <p
                    className={cn(
                      "text-grey-35 text-12 tablet:text-grey-65 tablet:font-normal tablet:tracking-half m-0 inline-block font-semibold",
                      wizardDataHeading.ring_size_tips ? "w-full" : "w-auto"
                    )}
                    data-cy={`product-wizard-heading-details-${wizardDataHeading.pref}`}
                    {...(hasHebrewChar(description) ? { dir: "auto" } : {})}
                  >
                    {description}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center">
              {!hidePrice && (
                <p
                  className="text-14 tracking-half mr-20 font-bold"
                  {...(wizardDataHeading.pref == "sku"
                    ? { "data-ge-parentproduct-base-price": true }
                    : { "data-ge-subproducts-subsequent-price": true })}
                >
                  {price}
                </p>
              )}
              {isOpened ? (
                <PlusMinus icon={lightMinus} />
              ) : (
                <PlusMinus icon={lightPlus} />
              )}
            </div>
          </Button>
        </h2>
      </div>
    </>
  )
}

const getBandColor = (state, ownProps) => {
  if (/b[0-9]+/.test(ownProps.pref)) {
    return state.choices[ownProps.pref].rgb
  }
}
const getRingData = (state, ownProps) => {
  if (ownProps.pref === "sz") {
    return state.wizard.data.sz
  } else {
    return null
  }
}

const getImage = (wizardData, state, ownProps) => {
  if (!wizardData) {
    return null
  }
  if (wizardData.view_type == "no-image") {
    return {
      x1: "",
      x2: "",
    }
  }
  let choiceData = state.choices[wizardData.pref]
  if (ownProps.pref == "diamond") {
    choiceData = state.choices.d_shape
  }
  if (/c[0-9]+$/.test(wizardData.pref)) {
    return builderImage(wizardData.pref, state.choices)
  } else if (wizardData.heading?.pref === "diamond") {
    const finalDiamond = state.diamondSelector.selected_diamond || null
    if (finalDiamond?.shape) {
      let shapeCode = finalDiamond.shape.toLowerCase()
      let stoneType = finalDiamond.stone_type
      return {
        x1: `${sl.config.jewlr_asset_url}images/product/diamond/${shapeCode}/${stoneType}.png`,
        x2: `${sl.config.jewlr_asset_url}images/product/diamond/${shapeCode}/${stoneType}_2x.png`,
      }
    } else {
      return {
        x1: `${sl.config.jewlr_asset_url}images/product/diamond/rd/04DIAM.png`,
        x2: `${sl.config.jewlr_asset_url}images/product/diamond/rd/04DIAM_2x.png`,
      }
    }
  } else if (choiceData && typeof choiceData.imageKey !== "undefined") {
    return imageLookup(wizardData.pref, choiceData.imageKey)
  } else if (
    typeof choiceData === "undefined" ||
    !choiceData.image ||
    wizardData.pref == "ch" ||
    /ch[0-9]+$/.test(wizardData.pref) ||
    wizardData.pref == "box"
  ) {
    return {
      x1: require("images/icons/jewlr.gif"),
      x2: require("images/icons/jewlr.gif"),
    }
  } else if (typeof choiceData.image.x2 === "undefined") {
    return {
      x1: choiceData.image,
      x2: choiceData.image,
    }
  } else {
    return choiceData.image
  }
}

const mapStateToProps = (state, ownProps) => {
  let wizardData = null
  wizardData = state.wizard.data[ownProps.pref]

  let wizardHeadingTitle = wizardData?.heading?.title
  if (wizardData?.heading?.titles?.options) {
    const titleOptions = filterProductOptionsV2(
      wizardData.heading.titles.options,
      state.choices
    )
    if (titleOptions.length) {
      wizardHeadingTitle = titleOptions[0].label
    }
  }

  return {
    bandColor: getBandColor(state, ownProps),
    dataRing: getRingData(state, ownProps),
    description: displayOptionDescription(wizardData, state, ownProps),
    hideMiniview: state.previewImageUI.miniview.scrollHide,
    hidePrice: state.wizard.metalsIncludeSurchargeFor.includes(ownProps.pref),
    image: getImage(wizardData, state, ownProps),
    isOpened: state.panel.openedPanelIndexes.indexOf(ownProps.pref) > -1,
    // prefOrder: state.wizard.prefOrder,
    prefStore: state.wizardUI.prefStore,
    price: displayOptionPrice(state, ownProps),
    scrollPanel: state.panel.scrollPanelIndex,
    selected: state.choices[ownProps.pref],
    wizardData,
    wizardHeadingTitle: wizardHeadingTitle,
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    removeScroll: () => {
      dispatch(scrollPanelIndex(""))
    },
    togglePanel: (panelPref) => {
      if (
        panelPref == "ef" ||
        panelPref == "ef2" ||
        /img[0-9]+/.test(panelPref)
      ) {
        dispatch(clearDefault())
      }
      dispatch(togglePanelIndex(panelPref, ownProps.pref))
    },
  }
}

InternalAccordionHeading.propTypes = {
  bandColor: PropTypes.string,
  dataRing: PropTypes.object,
  description: PropTypes.string,
  hideMiniview: PropTypes.bool,
  hidePrice: PropTypes.bool,
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isOpened: PropTypes.bool,
  pref: PropTypes.string,
  // prefOrder: PropTypes.array,
  price: PropTypes.string,
  removeScroll: PropTypes.func,
  scrollPanel: PropTypes.string,
  selected: PropTypes.object,
  togglePanel: PropTypes.func,
  wizardData: PropTypes.object,
  wizardHeadingTitle: PropTypes.string,
}

const AccordionHeading = connect(
  mapStateToProps,
  mapDispatchToProps
)(InternalAccordionHeading)

export default AccordionHeading
